import type { ComponentProps, ReactNode } from 'react';
import { Fragment } from 'react';
import { styled } from 'styled-components';

import { Box } from '#/components/Box/Box';
import { useMultiStep } from '#/components/Form/MultiStepForm/useMultiStep';
import { AnimatedCheckmark } from '#/components/Icon/AnimatedCheckmark';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { colors, transition } from '#/theme/theme';

import type { HeaderProps } from './MultiStepFormHeader';

type Props = Pick<HeaderProps, 'steps'> & {
  logo?: ReactNode;
};
const Step = styled(
  // TODO: Fix this the next time the file is edited.
  // oxlint-disable-next-line typescript/no-explicit-any
  ({ complete, index, active }: any) => {
    let color = colors.mediumContentGrey;
    if (complete) {
      color = 'white';
    } else if (active) {
      color = colors.brandBlue;
    }
    return (
      <Box
        display="flex"
        width={24}
        height={24}
        alignItems="center"
        justifyContent="center"
        borderRadius="50%"
        fontSize={12}
        transition={`all ${transition}`}
        bg={complete ? colors.brandBlue : 'transparent'}
        color={color}
        boxShadow={`0 0 0 2px ${active || complete ? colors.brandBlue : colors.selectionGrey} inset`}
      >
        {complete ? (
          <AnimatedCheckmark colorName="selectionGrey" size={24} />
        ) : (
          index + 1
        )}
      </Box>
    );
  },
)`
  /* stylelint */
`;

const StepLine = styled(({ ...props }: ComponentProps<typeof Box>) => (
  <Box
    position="relative"
    mx="4px"
    height="2px"
    width={[25, 50]}
    overflow="hidden"
    bg={colors.elementGrey}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
))<{ $complete?: boolean }>`
  &::before {
    content: '';
    position: absolute;
    right: 100%;
    width: 100%;
    height: 100%;
    background: ${colors.brandBlue};
    transform: translateX(${(props) => (props.$complete ? '100%' : 0)});
    transition: all ${transition};
  }
`;

export function SmallHeader({ steps, logo }: Props) {
  const { currentStep } = useMultiStep();

  const visibleSteps = steps
    .map((step, index) => ({ ...step, index }))
    .filter((step) => !step.ignoreInNav);
  return (
    <Box
      height={[null, null, null, 100]}
      py={[16, null, null, 24]}
      display="flex"
      justifyContent="space-between"
      borderBottom={`1px solid ${colors.selectionGrey}`}
    >
      <div style={{ lineHeight: 0 }}>
        {logo ?? <IdealistLogo withText height={{ mobile: 28, desktop: 52 }} />}
      </div>

      {visibleSteps.length > 1 && (
        <Box display="flex" alignItems="center">
          {visibleSteps.map((step, index) => (
            <Fragment key={step.index}>
              <Step
                active={currentStep === step.index}
                complete={currentStep > step.index}
                index={index}
              />
              {index < visibleSteps.length - 1 && (
                <StepLine $complete={currentStep > step.index} />
              )}
            </Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
}
